import React, { useCallback, useEffect, useRef, useState } from 'react';
import { HiOutlineSpeakerphone } from 'react-icons/hi';
import { FiClock } from 'react-icons/fi';
import {
  diaDaSemana,
  formatarData,
  formatarDataHora,
  msRestantes,
  obterStatusEncerramento,
  StatusEncerramento,
} from '~/config/encerramentoSistema';
import history from '~/services/history';
import ContadorRegressivo from './ContadorRegressivo';
import ModalComunicado, { PrazoComunicado } from './ModalComunicado';
import TelaAcessoEncerrado from './TelaAcessoEncerrado';
import {
  BotaoBarra,
  ContainerBarra,
  EstiloGlobalBarraAviso,
  IconeBarra,
  SeloContador,
  SeloContagem,
  TextoBarra,
} from './styles';

const INTERVALO_VERIFICACAO_MS = 60 * 1000;
/** Folga após o fim da liberação, para a verificação cair já depois do prazo. */
const FOLGA_FIM_LIBERACAO_MS = 500;
const CHAVE_COMUNICADO_VISTO = 'KONVIX_ENCERRAMENTO_COMUNICADO_VISTO';

const lerComunicadoVisto = (): string | null => {
  try {
    return localStorage.getItem(CHAVE_COMUNICADO_VISTO);
  } catch {
    return null;
  }
};

const marcarComunicadoVisto = (chave: string): void => {
  try {
    localStorage.setItem(CHAVE_COMUNICADO_VISTO, chave);
  } catch {
    // sem acesso ao storage: o comunicado volta a abrir no próximo carregamento
  }
};

const textoContagem = (diasAteUltimoDia: number): string => {
  if (diasAteUltimoDia === 0) return 'Hoje é o último dia';
  if (diasAteUltimoDia === 1) return 'Amanhã é o último dia';
  return `Faltam ${diasAteUltimoDia} dias`;
};

interface BarraAvisoProps {
  ultimoDiaDeAcesso: string;
  diasAteUltimoDia: number;
  onVerComunicado(): void;
}

const BarraAviso: React.FC<BarraAvisoProps> = ({
  ultimoDiaDeAcesso,
  diasAteUltimoDia,
  onVerComunicado,
}) => (
  <>
    <EstiloGlobalBarraAviso />
    <ContainerBarra role="status" aria-live="polite">
      <IconeBarra>
        <HiOutlineSpeakerphone size={18} aria-hidden />
      </IconeBarra>
      <TextoBarra>
        <span className="texto-completo">
          <strong>Comunicado importante:</strong> o acesso a este sistema será
          encerrado. Último dia de acesso:{' '}
          <strong>
            {diaDaSemana(ultimoDiaDeAcesso)}, {formatarData(ultimoDiaDeAcesso)}
          </strong>
        </span>
        <span className="texto-curto">
          <span className="prefixo-curto">
            <strong>Encerramento do sistema</strong> ·{' '}
          </span>
          Último dia de acesso:{' '}
          <strong>{formatarData(ultimoDiaDeAcesso)}</strong>
        </span>
      </TextoBarra>
      <SeloContagem urgente={diasAteUltimoDia <= 2}>
        {textoContagem(diasAteUltimoDia)}
      </SeloContagem>
      <BotaoBarra type="button" onClick={onVerComunicado}>
        Ver comunicado
      </BotaoBarra>
    </ContainerBarra>
  </>
);

interface BarraLiberacaoProps {
  fimLiberacao: number;
  onVerComunicado(): void;
}

// Sem aria-live: o timer muda a cada segundo e seria anunciado sem parar.
const BarraLiberacao: React.FC<BarraLiberacaoProps> = ({
  fimLiberacao,
  onVerComunicado,
}) => (
  <>
    <EstiloGlobalBarraAviso />
    <ContainerBarra role="region" aria-label="Liberação temporária de acesso">
      <IconeBarra>
        <FiClock size={18} aria-hidden />
      </IconeBarra>
      <TextoBarra>
        <span className="texto-completo">
          <strong>Acesso liberado temporariamente:</strong> o sistema será
          encerrado em <strong>{formatarDataHora(fimLiberacao, true)}</strong>
        </span>
        <span className="texto-curto">
          <span className="prefixo-curto">
            <strong>Liberação temporária</strong> ·{' '}
          </span>
          Acesso até <strong>{formatarDataHora(fimLiberacao)}</strong>
        </span>
      </TextoBarra>
      <SeloContador urgente>
        <span className="rotulo-selo">Encerra em </span>
        <ContadorRegressivo fim={fimLiberacao} />
      </SeloContador>
      <BotaoBarra type="button" onClick={onVerComunicado}>
        Ver comunicado
      </BotaoBarra>
    </ContainerBarra>
  </>
);

/**
 * Aviso de encerramento do sistema, liberação temporária e bloqueio após o
 * prazo, conforme `~/config/encerramentoSistema`. Para hosts não configurados,
 * apenas renderiza os filhos.
 */
const EncerramentoSistema: React.FC = ({ children }) => {
  const [status, setStatus] = useState<StatusEncerramento>(
    obterStatusEncerramento,
  );
  const [comunicadoAberto, setComunicadoAberto] = useState(false);
  const situacaoAnterior = useRef(status.situacao);

  const atualizar = useCallback(() => {
    const novoStatus = obterStatusEncerramento();
    setStatus((atual) =>
      JSON.stringify(atual) === JSON.stringify(novoStatus) ? atual : novoStatus,
    );
  }, []);

  useEffect(() => {
    const intervalo = window.setInterval(atualizar, INTERVALO_VERIFICACAO_MS);
    const pararDeOuvirRotas = history.listen(atualizar);
    document.addEventListener('visibilitychange', atualizar);

    return () => {
      window.clearInterval(intervalo);
      pararDeOuvirRotas();
      document.removeEventListener('visibilitychange', atualizar);
    };
  }, [atualizar]);

  // Prazo encerrado com o sistema aberto: recarrega para que o useAuth limpe a
  // sessão e a tela de encerramento seja exibida desde o início.
  useEffect(() => {
    if (
      status.situacao === 'encerrado' &&
      situacaoAnterior.current !== 'encerrado'
    ) {
      window.onbeforeunload = null;
      window.location.replace('/');
    }
    situacaoAnterior.current = status.situacao;
  }, [status.situacao]);

  const aviso = status.situacao === 'aviso' ? status : null;
  const liberacao = status.situacao === 'liberacaoTemporaria' ? status : null;
  const fimLiberacao = liberacao?.fimLiberacao;

  // Encerra exatamente no fim da liberação, sem esperar a verificação de 60s.
  useEffect(() => {
    if (fimLiberacao === undefined) return undefined;

    const temporizador = window.setTimeout(
      atualizar,
      msRestantes(fimLiberacao) + FOLGA_FIM_LIBERACAO_MS,
    );

    return () => window.clearTimeout(temporizador);
  }, [fimLiberacao, atualizar]);

  // Abre o comunicado automaticamente uma vez por dia no aviso e uma vez por
  // liberação temporária. No PDV online ele não abre sozinho (a tela já abre
  // modais próprios), mas a barra continua visível.
  let chaveComunicado: string | undefined;
  if (aviso) chaveComunicado = aviso.hoje;
  if (liberacao) chaveComunicado = `liberacao:${liberacao.fimLiberacao}`;

  useEffect(() => {
    if (
      !chaveComunicado ||
      history.location.pathname.startsWith('/pdv-online')
    ) {
      return;
    }
    if (lerComunicadoVisto() === chaveComunicado) return;

    marcarComunicadoVisto(chaveComunicado);
    setComunicadoAberto(true);
  }, [chaveComunicado]);

  if (status.situacao === 'encerrado') {
    return (
      <TelaAcessoEncerrado
        ultimoDiaDeAcesso={status.ultimoDiaDeAcesso}
        fimLiberacao={status.fimLiberacao}
      />
    );
  }

  let prazo: PrazoComunicado | null = null;
  if (aviso) {
    prazo = {
      tipo: 'aviso',
      ultimoDiaDeAcesso: aviso.ultimoDiaDeAcesso,
      diasAteUltimoDia: aviso.diasAteUltimoDia,
    };
  } else if (liberacao) {
    prazo = {
      tipo: 'liberacao',
      inicioLiberacao: liberacao.inicioLiberacao,
      fimLiberacao: liberacao.fimLiberacao,
    };
  }

  const abrirComunicado = () => setComunicadoAberto(true);

  // Mantém os filhos sempre na mesma posição para não remontar as rotas
  // quando o aviso aparece ou some.
  return (
    <>
      {aviso && (
        <BarraAviso
          ultimoDiaDeAcesso={aviso.ultimoDiaDeAcesso}
          diasAteUltimoDia={aviso.diasAteUltimoDia}
          onVerComunicado={abrirComunicado}
        />
      )}
      {liberacao && (
        <BarraLiberacao
          fimLiberacao={liberacao.fimLiberacao}
          onVerComunicado={abrirComunicado}
        />
      )}
      {children}
      {prazo && (
        <ModalComunicado
          show={comunicadoAberto}
          onClose={() => setComunicadoAberto(false)}
          prazo={prazo}
        />
      )}
    </>
  );
};

export default EncerramentoSistema;
